import React, { ChangeEvent, Suspense, useCallback, useState } from 'react';

import { Box } from '@rbilabs/components-library';
import { isNil } from 'lodash-es';
import { useIntl } from 'react-intl';

import { OnClick } from '@rbi-ctg/frontend';
import ActionButton from 'components/action-button';
import BoxInput from 'components/box-input';
import { IIconCheckCircleProps } from 'components/icons/check-circle';
import IconCreditCard from 'components/icons/minimal-credit-card';
import { LazyComponent, lazyWithNullFallback } from 'components/layout/util';
import { MobileSpacer } from 'components/mobile-spacer';
import Modal, { ModalContent, ModalHeading } from 'components/modal';
import Picture from 'components/picture';
import UserVerificationModal from 'components/user-verification-modal';
import { usePrepaidTransactionCurrencyCodesQuery } from 'generated/rbi-graphql';
import { usePrepaidsBalance } from 'hooks/prepaid/use-prepaids-balance';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useFeatureLayout } from 'hooks/use-feature-layout';
import useUserHasToVerify from 'hooks/use-user-has-to-verify';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { usePaymentContext } from 'state/payment';
import { useUIContext } from 'state/ui';
import { ENABLE_NO_PIN_CHECK_PREPAID_MERGE } from 'utils/constants';
import { routes } from 'utils/routing';

import {
  BottomSection,
  CardNumberContainer,
  CenteredContent,
  CenteredContentWrapper,
  ContentWrapper,
  ErrorAnchor,
  ErrorText,
  Form,
  FormInputs,
  HeadingText,
  NormalText,
  PinContainer,
  PlacementHolder,
  TextContainer,
  TimCardContainer,
  TopSection,
} from './styled';

const IconCheckCircle: LazyComponent<IIconCheckCircleProps> = lazyWithNullFallback(() =>
  import('components/icons/check-circle')
);

enum ModalStates {
  FORM = 'FORM',
  REVIEW = 'REVIEW',
  CONFIRM = 'CONFIRM',
}
interface ITransferBalanceModal {
  onDismiss: OnClick;
  modalAppearanceEventMessage?: string;
}

const INITIAL_SOURCE_CARD_DETAILS = {
  cardNumber: '',
  pin: '',
  currentBalance: 0,
};

const VALID_CARD_NUMBER_LENGTH = 16;

/**
 * @todo
 * re-implement form using Formik and superstruct
 * add validation
 * break up into smaller components
 */
const TransferBalanceModal = ({
  onDismiss,
  modalAppearanceEventMessage = 'Transfer prepaid card balance',
}: ITransferBalanceModal) => {
  const { formatMessage } = useIntl();
  const {
    getPrepaidPaymentMethod,
    mergePrepaidCardBalances,
    loading: paymentContextLoading,
  } = usePaymentContext();
  const { formatCurrencyForLocale } = useUIContext();
  const enableUnverifiedSavedPayments = useFlag(LaunchDarklyFlag.ENABLE_UNVERIFIED_SAVED_PAYMENTS);
  const userHasToVerify = useUserHasToVerify(enableUnverifiedSavedPayments);
  const { region } = useLocale();
  const { featureLayout } = useFeatureLayout();
  const [section, setSection] = useState(ModalStates.FORM);
  const [showBalanceLookupError, setShowBalanceLookupError] = useState(false);
  const [showTransferBalanceError, setShowTransferBalanceError] = useState(false);
  const [sourceCardDetails, setSourceCardDetails] = useState<{
    cardNumber: string;
    pin: string;
    currentBalance: number;
  }>(INITIAL_SOURCE_CARD_DETAILS);
  const {
    currentBalance,
    error: getBalanceError,
    getPrepaidsBalance,
    loading: getBalanceLoading,
  } = usePrepaidsBalance({ region });
  const loading = getBalanceLoading || paymentContextLoading;

  const prepaidPaymentMethod = getPrepaidPaymentMethod();
  const prepaidPaymentMethodBalance = formatCurrencyForLocale(
    (prepaidPaymentMethod && prepaidPaymentMethod.prepaid!.currentBalance) || 0
  );

  // currentBalance is fine here as the prepaidBalance query passes countryCode
  const formattedBalanceToTransfer = formatCurrencyForLocale(sourceCardDetails.currentBalance);

  const handleCardNumberChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setSourceCardDetails(currentCardDetails => ({ ...currentCardDetails, cardNumber: value }));
    },
    []
  );

  const handlePinChange = useCallback(({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setSourceCardDetails(currentCardDetails => ({ ...currentCardDetails, pin: value }));
  }, []);

  const handleGetCardBalance = useCallback(
    e => {
      e.preventDefault();

      // replace all `-` and spaces with an empty string
      const cardNumber = sourceCardDetails.cardNumber.replace(/[-\s]/g, '');

      return getPrepaidsBalance({ cardNumber });
    },
    [getPrepaidsBalance, sourceCardDetails.cardNumber]
  );

  const sourceCardNumber = sourceCardDetails.cardNumber;
  const prepaidCardNumber =
    (prepaidPaymentMethod &&
      prepaidPaymentMethod.prepaid &&
      prepaidPaymentMethod.prepaid.cardNumber) ||
    '';
  const {
    data: sourceCardTransactionCurrencyCodesData,
    loading: sourceCardTransactionCurrencyCodesLoading,
    error: sourceCardTransactionCurrencyCodesError,
  } = usePrepaidTransactionCurrencyCodesQuery({
    variables: { cardNumber: sourceCardNumber },
    // Only fetch the card for currency validation if the card number length is a valid one
    skip: sourceCardNumber.length !== VALID_CARD_NUMBER_LENGTH,
  });
  const {
    data: prepaidCardTransactionCurrencyCodesData,
    loading: prepaidCardTransactionCurrencyCodesLoading,
  } = usePrepaidTransactionCurrencyCodesQuery({
    variables: { cardNumber: prepaidCardNumber },
  });
  const currencyCodesLoading =
    prepaidCardTransactionCurrencyCodesLoading || sourceCardTransactionCurrencyCodesLoading;
  const currencyCodesData =
    !!prepaidCardTransactionCurrencyCodesData && !!sourceCardTransactionCurrencyCodesData;
  const sourceCardCurrencyCode =
    sourceCardTransactionCurrencyCodesData?.prepaidsTransactions?.transactions?.[0]?.currencyCode
      ?.number;
  const prepaidCardCurrencyCode =
    prepaidCardTransactionCurrencyCodesData?.prepaidsTransactions?.transactions?.[0]?.currencyCode
      ?.number;
  const cardCurrenciesMatch = prepaidCardCurrencyCode === sourceCardCurrencyCode;
  const cardCurrencyError =
    currencyCodesData &&
    !currencyCodesLoading &&
    !sourceCardTransactionCurrencyCodesError &&
    !cardCurrenciesMatch;

  const handleSubmit = useCallback(() => {
    mergePrepaidCardBalances({
      destinationFdAccountId: prepaidPaymentMethod && prepaidPaymentMethod.fdAccountId,
      sourceCardNumber: sourceCardDetails.cardNumber,
      sourcePin: ENABLE_NO_PIN_CHECK_PREPAID_MERGE ? null : sourceCardDetails.pin,
    })
      .then(() => {
        setSection(ModalStates.CONFIRM);
      })
      .catch(() => {
        setShowTransferBalanceError(true);
      });
  }, [
    mergePrepaidCardBalances,
    prepaidPaymentMethod,
    sourceCardDetails.cardNumber,
    sourceCardDetails.pin,
  ]);

  const handleConfirm = useCallback(
    e => {
      setSourceCardDetails(INITIAL_SOURCE_CARD_DETAILS);
      onDismiss(e);
    },
    [onDismiss]
  );

  useEffectOnUpdates(() => {
    setShowBalanceLookupError(!loading && !!getBalanceError);
    if (!isNil(currentBalance)) {
      setSourceCardDetails(currentDetails => ({ ...currentDetails, currentBalance }));
      setSection(ModalStates.REVIEW);
    }
  }, [getBalanceError, currentBalance]);

  const errorMessage = formatMessage(
    { id: 'transferBalanceError' },
    {
      link: <ErrorAnchor href={routes.support}>{formatMessage({ id: 'chatWithUs' })}</ErrorAnchor>,
    }
  );

  const giftCardImage = featureLayout?.giftCardImage?.locale;

  return userHasToVerify ? (
    <UserVerificationModal onDismiss={onDismiss} />
  ) : (
    <Modal
      onDismiss={onDismiss}
      mParticleEventData={{
        modalAppearanceEventMessage,
      }}
      data-testid="transfert-balance-modal"
    >
      <ModalContent>
        <ModalHeading data-testid="transfert-balance-modal-heading">
          {formatMessage({ id: 'transferBalance' })}
        </ModalHeading>
        {section === ModalStates.FORM && (
          <ContentWrapper data-testid="form-section-content">
            <Suspense fallback={<PlacementHolder height="250px" width="250px" />}>
              <TimCardContainer>
                {giftCardImage && (
                  <Picture
                    image={giftCardImage}
                    alt={formatMessage({ id: 'giftCardImageAltText' })}
                    objectFitContain
                  />
                )}
              </TimCardContainer>
            </Suspense>
            <NormalText margin="1rem 0 0 0">
              {formatMessage({ id: 'transferBalanceDirections' })}
            </NormalText>

            <Form onSubmit={handleGetCardBalance}>
              <FormInputs>
                <CardNumberContainer>
                  <BoxInput
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    value={sourceCardDetails.cardNumber}
                    onChange={handleCardNumberChange}
                    renderIcon={() => <IconCreditCard height={20} />}
                    required
                    data-testid="transfert-card-number-input"
                    maxLength={16}
                  />
                </CardNumberContainer>
                <PinContainer>
                  <BoxInput
                    placeholder="PIN"
                    value={sourceCardDetails.pin}
                    onChange={handlePinChange}
                    required
                    data-testid="transfert-card-pin-input"
                    maxLength={8}
                  />
                </PinContainer>
              </FormInputs>
              {showBalanceLookupError && <ErrorText>{errorMessage}</ErrorText>}
              {cardCurrencyError && (
                <ErrorText>{formatMessage({ id: 'cardCurrenciesDontMatch' })}</ErrorText>
              )}
              <Box width="100%" center>
                <ActionButton
                  type="submit"
                  isLoading={loading || currencyCodesLoading}
                  disabled={loading || currencyCodesLoading || cardCurrencyError}
                  data-testid="submit-transfert-card-btn"
                >
                  {formatMessage({ id: 'continue' })}
                </ActionButton>
              </Box>
              <MobileSpacer />
            </Form>
          </ContentWrapper>
        )}

        {section === ModalStates.REVIEW && (
          <ContentWrapper data-testid="review-section-content">
            <TopSection>
              <Suspense fallback={<PlacementHolder height="91.35px" width="120.05px" />}>
                <TimCardContainer>
                  {giftCardImage && <Picture image={giftCardImage} alt="" objectFitContain />}
                </TimCardContainer>
              </Suspense>
              <TextContainer>
                <HeadingText>{formattedBalanceToTransfer}</HeadingText>
                <NormalText textAlign="left" data-testid="card-x-has-a-balance-of">
                  {formatMessage(
                    { id: 'cardXHasABalanceOfY' },
                    {
                      cardNumber: sourceCardDetails.cardNumber,
                      balance: formattedBalanceToTransfer,
                    }
                  )}
                </NormalText>
              </TextContainer>
            </TopSection>

            <BottomSection>
              <NormalText margin="1rem 0">
                {formatMessage(
                  { id: 'yourBalanceXAddY' },
                  {
                    currentBalance: prepaidPaymentMethodBalance,
                    balanceToAdd: formattedBalanceToTransfer,
                  }
                )}
              </NormalText>
              {showTransferBalanceError && <ErrorText>{errorMessage}</ErrorText>}
              <Box width="100%" center minWidth="auto">
                <ActionButton
                  fullWidth
                  onlyIcon
                  onClick={handleSubmit}
                  isLoading={loading}
                  disabled={loading}
                  data-testid="transfer-to-gift-card-btn"
                >
                  {formatMessage(
                    { id: 'transferXToMyGiftCard' },
                    {
                      balance: formattedBalanceToTransfer,
                    }
                  )}
                </ActionButton>
              </Box>
            </BottomSection>
          </ContentWrapper>
        )}

        {section === ModalStates.CONFIRM && (
          <CenteredContentWrapper>
            <CenteredContent>
              <Suspense fallback={<PlacementHolder height="57px" width="57px" />}>
                <IconCheckCircle width="57" height="57" />
              </Suspense>
              <HeadingText margin="1.5rem 0 0">{formatMessage({ id: 'success' })}</HeadingText>
              <NormalText margin="0 0 3.5rem">
                {formatMessage(
                  { id: 'yourNewBalanceX' },
                  {
                    newBalance: prepaidPaymentMethodBalance,
                  }
                )}
              </NormalText>
              <Box width="100%" center>
                <ActionButton onClick={handleConfirm}>
                  {formatMessage({ id: 'closeWindow' })}
                </ActionButton>
              </Box>
            </CenteredContent>
          </CenteredContentWrapper>
        )}
      </ModalContent>
    </Modal>
  );
};

export default TransferBalanceModal;
